import React from 'react'
import { gql } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import type { Adjustment, RouteDef } from '@ztechapps/common.contract'
import { useDocument } from '@ztechapps/common.hook'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
import { LocalDate, LocalDateTime } from '@ztechapps/webui.date'
import { Tags } from '@ztechapps/webui.input'
import { EntityCodeLink, EntityPreview } from '@ztechapps/webui.util'
import { AdjustmentStatusBadge } from '../StatusBadge'

const adjustmentPreviewQuery = gql`
  query singleAdjustment($filter: JSON) {
    singleAdjustment(filter: $filter) {
      _id
      code
      status
      createdAt
      completionDate
      tags
      items {
        _id
        quantity
        product {
          _id
          code
          name
        }
      }
    }
  }
`

const AdjustmentPreview = ({ _id, code }: { _id: string; code: string }) => {
  const adjustmentDocumentResult = useDocument<Adjustment>({ _id, query: adjustmentPreviewQuery })
  const adjustment = adjustmentDocumentResult.document

  return (
    <EntityPreview
      code={code}
      statusBadge={<AdjustmentStatusBadge status={adjustment?.status} />}
      loading={adjustmentDocumentResult.loading}
      rows={[
        // `LocalDate`/`LocalDateTime` always render SOMETHING (their own "--" for a missing
        // date), so `EntityPreview`'s `?? '---'` fallback never sees a falsy value to catch —
        // checked here instead, same as `WorkOrderCodeLink`'s Tags row.
        {
          label: 'Date Added',
          value: adjustment?.createdAt ? <LocalDateTime value={adjustment?.createdAt} /> : undefined,
        },
        {
          label: 'Completion Date',
          value: adjustment?.completionDate ? <LocalDate value={adjustment?.completionDate} /> : undefined,
        },
        {
          label: 'Tags',
          // `Tags` renders an empty (but truthy) wrapper for an empty array, so `EntityPreview`'s
          // own `?? '---'` fallback never sees a falsy value to catch — checked here instead.
          value: isEmpty(adjustment?.tags) ? undefined : (
            <Tags dataArray={adjustment?.tags} className="justify-content-end" />
          ),
        },
      ]}
      list={{
        label: 'Items',
        items: map(adjustment?.items, (item) => (
          <>
            {item?.product?.code} - {item?.product?.name} [{item?.quantity}]
          </>
        )),
      }}
    />
  )
}

export type AdjustmentCodeLinkProps = { _id: string; code: string }

export const AdjustmentCodeLink = ({ _id, code }: AdjustmentCodeLinkProps) => {
  const adjustmentItemRoute = useInject<RouteDef>(WebuiIocKey.routes.adjustmentItem)

  return (
    <EntityCodeLink
      code={code}
      to={adjustmentItemRoute.altPath({ _id })}
      target="_blank"
      preview={<AdjustmentPreview _id={_id} code={code} />}
    />
  )
}
